'use client';

import { cn } from '../../lib/utils';
import { hoodiePaths } from './legacyPaths';

const PE_AUTO = { pointerEvents: 'auto' } as const;

interface LegacyRainaHornInnerProps {
  svgId: string;
  /** Suffix for gradient/mask IDs to avoid conflicts when multiple instances exist */
  idSuffix?: string;
}

/**
 * Inner horn rendering without wrapper divs.
 * Used by both LegacyRainaHead (inline) and LegacyRainaHorn (standalone for badge variant).
 */
export function LegacyRainaHornInner({ svgId, idSuffix = '' }: LegacyRainaHornInnerProps) {
  const id = (name: string) => `${svgId}${name}${idSuffix}`;

  return (
    <div className="absolute inset-[0%_39.36%_81.8%_39.89%] pointer-events-none">
      <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 88.9561 119.214">
        <defs>
          <linearGradient gradientUnits="userSpaceOnUse" id={id('hornGrad')} x1="37.5663" x2="130.33" y1="65.1644" y2="104.646">
            <stop stopColor="#762BFA" />
            <stop offset="1" stopColor="#33116F" />
          </linearGradient>
          <mask id={id('hornMask')} maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
            <path d={hoodiePaths.hornMask} fill="#F97DF3" />
          </mask>
        </defs>
        <path d={hoodiePaths.hornBase} fill={`url(#${id('hornGrad')})`} data-region="head" style={PE_AUTO} />
        <g mask={`url(#${id('hornMask')})`}>
          <path d={hoodiePaths.hornStripe1} stroke="#33116F" strokeWidth="7.6664" />
          <path d={hoodiePaths.hornStripe2} stroke="#33116F" strokeWidth="7.6664" />
          <path d={hoodiePaths.hornStripe3} stroke="#33116F" strokeWidth="7.6664" />
        </g>
      </svg>
    </div>
  );
}

interface LegacyRainaHornProps {
  svgId: string;
  isNodding?: boolean;
  isHeadTilting?: boolean;
  headTiltDirection?: 'left' | 'right';
}

/**
 * Standalone horn rendering component for use in badge variant.
 * Renders horn separately so it can be layered outside the hexagon clip path.
 * Includes head animation wrapper for proper synchronization.
 */
export function LegacyRainaHorn({ svgId, isNodding = false, isHeadTilting = false, headTiltDirection = 'right' }: LegacyRainaHornProps) {
  // Match head animation class for horn movement sync
  const headAnimationClass = isNodding
    ? 'motion-safe:animate-[raina-nod_0.7s_ease-in-out]'
    : isHeadTilting
      ? headTiltDirection === 'right'
        ? 'motion-safe:animate-[rainaHeadTiltRight_1s_ease-in-out]'
        : 'motion-safe:animate-[rainaHeadTiltLeft_1s_ease-in-out]'
      : '';

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className={cn('absolute inset-0 pointer-events-none', headAnimationClass)}
        style={{ transformOrigin: '50% 70%', willChange: 'transform' }}
      >
        <LegacyRainaHornInner svgId={svgId} idSuffix="Badge" />
      </div>
    </div>
  );
}
